// WOS-BUNDLE: company tags in Work (Work OS task-detail.tsx tags editor, container-view.tsx tag
// filter). One `tags.list` call names and colours every tag; cards carry `tag_ids`. The task
// drawer's Tags row shows the chips (hover × removes one) and a visible "+" that opens a picker:
// type to filter the company's tags, pick one, or `Create "name"` (Enter tags by the typed name,
// made when no tag has it, ignoring case). The bulk bar tags or untags the selection; the board's
// filter menu filters by tag. Every write shows at once and undoes itself on a refusal.
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TagChip,
  TagSwatch,
} from "@companyos/ui";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, TagIcon } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { useOptimisticMutation } from "../../api/optimistic";
import type { CompanyAccess } from "../company";
import { canCreateTag, cleanTagName, type TagView, tagOptions } from "./tag-logic";
import { updateTask } from "./task-cache";
import type { TaskDetail, ViewFilter } from "./view-engine";
import { inValues, withIn } from "./view-engine";
import { pendingAsNull, workKeys } from "./work-queries";

export const tagKeys = {
  all: (co: string) => [...workKeys.all(co), "tags"] as const,
};

/** The company's tags by name, with their task counts; none while the route is pending. */
export const tagsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: tagKeys.all(co),
    queryFn: async () =>
      (await pendingAsNull(() => api.client.tags.list({ params: { co } })))?.data ?? [],
    staleTime: 30_000,
  });

/** The tags, and each by id (chips on cards and rows). */
export function useTags(api: Api, co: string) {
  const q = useQuery(tagsQuery(api, co));
  const list = q.data ?? [];
  const byId = useMemo(() => new Map((q.data ?? []).map((t) => [t.id, t] as const)), [q.data]);
  return { list, byId, loaded: q.isSuccess };
}

/** Tag and untag one task, on screen at once (the card everywhere and the drawer). */
export function useTaskTagWrites(api: Api, co: string) {
  const qc = useQueryClient();
  const setTags = (taskId: string, fn: (ids: string[]) => string[]) => {
    updateTask(qc, co, taskId, (t) => ({ ...t, tag_ids: fn(t.tag_ids ?? []) }));
    const d = qc.getQueryData<TaskDetail>(workKeys.task(co, taskId));
    if (d) qc.setQueryData(workKeys.task(co, taskId), { ...d, tag_ids: fn(d.tag_ids ?? []) });
  };
  const add = useOptimisticMutation({
    keys: () => [workKeys.all(co)],
    apply: (v: { taskId: string; tag?: TagView; name?: string }) => {
      const id = v.tag?.id;
      if (id) setTags(v.taskId, (ids) => (ids.includes(id) ? ids : [...ids, id]));
    },
    mutationFn: (v) =>
      api.client.tasks.addTag({
        params: { co, id: v.taskId },
        body: v.tag ? { tag_id: v.tag.id } : { name: v.name ?? "" },
      }),
    reconcile: (tag, v) =>
      setTags(v.taskId, (ids) => (ids.includes(tag.id) ? ids : [...ids, tag.id])),
    settle: () => [tagKeys.all(co)],
  });
  const remove = useOptimisticMutation({
    keys: () => [workKeys.all(co)],
    apply: (v: { taskId: string; tagId: string }) =>
      setTags(v.taskId, (ids) => ids.filter((x) => x !== v.tagId)),
    mutationFn: (v) =>
      api.client.tasks.removeTag({ params: { co, id: v.taskId, item_id: v.tagId } }),
    settle: () => [tagKeys.all(co)],
  });
  return {
    addTag: (taskId: string, tag: TagView) => add.mutate({ taskId, tag }),
    addByName: (taskId: string, name: string) => add.mutate({ taskId, name }),
    remove: (taskId: string, tagId: string) => remove.mutate({ taskId, tagId }),
  };
}

/**
 * The picker: a search box over the company's tags, the matching ones, and `Create "name"` for a
 * new name. Enter takes the only match, else tags by the typed name.
 */
export function TagPicker({
  tags,
  taken,
  onPick,
  onCreate,
  trigger,
  label = "Add a tag",
}: {
  tags: readonly TagView[];
  taken: readonly string[];
  onPick: (tag: TagView) => void;
  onCreate: (name: string) => void;
  trigger: ReactElement;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const options = tagOptions(tags, q, taken).slice(0, 8);
  const create = canCreateTag(tags, q);
  const done = () => {
    setQ("");
    setOpen(false);
  };
  const submit = () => {
    const name = cleanTagName(q);
    if (!name) return;
    const exact = tags.find((t) => t.name.toLowerCase() === name.toLowerCase());
    if (exact) {
      if (!taken.includes(exact.id)) onPick(exact);
    } else onCreate(name);
    done();
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={trigger} />
      <PopoverContent align="start" className="w-64 gap-1 p-1.5" data-slot="tag-picker">
        <Input
          aria-label={label}
          placeholder="Tag name"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
        />
        <ul aria-label="Tags" className="m-0 flex max-h-64 list-none flex-col overflow-y-auto p-0">
          {options.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                onClick={() => {
                  onPick(t);
                  done();
                }}
              >
                <TagChip name={t.name} color={t.color} />
                <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                  {t.task_count}
                </span>
              </button>
            </li>
          ))}
          {create ? (
            <li>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                onClick={submit}
              >
                <PlusIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
                Create “{cleanTagName(q)}”
              </button>
            </li>
          ) : null}
          {!options.length && !create ? (
            <li className="px-2 py-1.5 text-sm text-muted-foreground">
              {tags.length ? "No other tag matches." : "Type a name to make the first tag."}
            </li>
          ) : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/** The drawer's Tags row: chips (hover × removes), and a visible "+" picker. */
export function TaskTagsSection({
  api,
  company,
  task,
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
}) {
  const co = company.id;
  const tags = useTags(api, co);
  const writes = useTaskTagWrites(api, co);
  const mine = (task.tag_ids ?? []).flatMap((id) => {
    const t = tags.byId.get(id);
    return t ? [t] : [];
  });
  return (
    <section
      aria-label="Tags"
      data-slot="task-tags"
      className="mt-2 flex flex-wrap items-center gap-1.5"
    >
      <TagIcon aria-hidden="true" className="size-3.5 text-faint" />
      {mine.map((t) => (
        <TagChip
          key={t.id}
          name={t.name}
          color={t.color}
          onRemove={() => writes.remove(task.id, t.id)}
        />
      ))}
      <TagPicker
        tags={tags.list}
        taken={task.tag_ids ?? []}
        onPick={(t) => writes.addTag(task.id, t)}
        onCreate={(name) => writes.addByName(task.id, name)}
        trigger={
          <Button
            variant="ghost"
            size="xs"
            className="h-[22px] gap-1 px-1.5 text-muted-foreground hover:text-foreground"
            aria-label="Add a tag"
          >
            <PlusIcon aria-hidden="true" />
            {mine.length ? null : "Add tag"}
          </Button>
        }
      />
    </section>
  );
}

/** The bulk bar's Tag menu: add or remove a tag on every selected task. */
export function BulkTagMenu({
  api,
  co,
  disabled,
  onAction,
}: {
  api: Api;
  co: string;
  disabled?: boolean;
  onAction: (a: { kind: "tag"; tag_id: string; op: "add" | "remove" }) => void;
}) {
  const tags = useTags(api, co);
  if (!tags.list.length) return null;
  const items = (op: "add" | "remove") =>
    tags.list.map((t) => (
      <DropdownMenuItem key={t.id} onClick={() => onAction({ kind: "tag", tag_id: t.id, op })}>
        <TagSwatch color={t.color} className="size-3" />
        {t.name}
      </DropdownMenuItem>
    ));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={<Button variant="ghost" size="sm" disabled={disabled} />}>
        <TagIcon aria-hidden="true" />
        Tag
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-48">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Add tag</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
            {items("add")}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Remove tag</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
            {items("remove")}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * The filter menu's Tags group: tick tags (`tag in [...]`), or "No tag" (`tag empty`). Ticking a
 * tag clears "No tag" and the other way round (they cannot both hold).
 */
export function TagFilterGroup({
  api,
  co,
  filter,
  onChange,
}: {
  api: Api;
  co: string;
  filter: ViewFilter;
  onChange: (f: ViewFilter) => void;
}) {
  const tags = useTags(api, co);
  if (!tags.list.length) return null;
  const ticked = inValues(filter, "tag");
  const noTag = (filter.all ?? []).some((c) => c.field === "tag" && c.op === "empty");
  const withoutEmpty = (f: ViewFilter): ViewFilter => {
    const all = (f.all ?? []).filter((c) => !(c.field === "tag" && c.op === "empty"));
    const { all: _a, ...rest } = f;
    return all.length ? { ...rest, all } : rest;
  };
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuGroup>
        <DropdownMenuLabel>Tags</DropdownMenuLabel>
        {tags.list.map((t) => (
          <DropdownMenuCheckboxItem
            key={t.id}
            checked={ticked.includes(t.id)}
            onCheckedChange={(on) =>
              onChange(
                withIn(
                  withoutEmpty(filter),
                  "tag",
                  on ? [...ticked, t.id] : ticked.filter((x) => x !== t.id),
                ),
              )
            }
          >
            <TagSwatch color={t.color} className="size-3" />
            {t.name}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuCheckboxItem
          checked={noTag}
          onCheckedChange={(on) => {
            const base = withoutEmpty(withIn(filter, "tag", []));
            onChange(
              on ? { ...base, all: [...(base.all ?? []), { field: "tag", op: "empty" }] } : base,
            );
          }}
        >
          No tag
        </DropdownMenuCheckboxItem>
      </DropdownMenuGroup>
    </>
  );
}

/** The active-filter chip text of a tag condition ("Tags: Sales, Q4 push" / "No tag"). */
export function tagFilterLabel(
  byId: ReadonlyMap<string, { name: string }>,
  c: { op: string; value?: unknown },
): string | null {
  if (c.op === "empty") return "No tag";
  if (c.op === "not_empty") return "Has a tag";
  const ids = Array.isArray(c.value) ? (c.value as string[]) : [];
  const names = ids.map((id) => byId.get(id)?.name ?? "a deleted tag").join(", ");
  if (c.op === "in") return `Tags: ${names}`;
  if (c.op === "nin") return `Not tagged: ${names}`;
  return null;
}
